import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Sun, Moon, Eye, EyeOff, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import NotificationBell from './NotificationBell';
import HeaderUserMenu from './HeaderUserMenu';
import FlagIcon from './voice/FlagIcon';
import DarkTooltip from './ui/DarkTooltip';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cache do perfil em nível de módulo: cada página monta seu próprio <Header />,
// e o refetch a cada navegação fazia a pill do usuário nascer vazia e "inchar"
// quando o /profile respondia, EMPURRANDO os botões da direita (privacidade,
// idioma, sino, tema). Com o cache, navegações renderizam a pill instantânea
// (largura estável) e o refresh acontece em silêncio.
let _profileCache = null;

// Idiomas suportados — bandeiras reais via FlagIcon (lib flag-icons, CSS global).
// Labels são endônimos fixos (não traduzir).
const LANGUAGES = [
  { code: 'pt', flagCode: 'br', label: 'Português (Brasil)' },
  { code: 'en', flagCode: 'us', label: 'English' },
  { code: 'es', flagCode: 'es', label: 'Español' },
];

// Botão redondo de ícone do header (tema, idioma) — compacto, sem glow.
const RoundIconButton = ({ onClick, ariaLabel, active, size = 36, children }) => (
  <button
    onClick={onClick}
    aria-label={ariaLabel}
    title={ariaLabel}
    style={{
      width: `${size}px`,
      height: `${size}px`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: active ? 'var(--dp-bg-card)' : 'transparent',
      border: `1px solid ${active ? 'var(--dp-blue-500)' : 'var(--dp-border-medium)'}`,
      borderRadius: '50%',
      color: 'var(--dp-text-secondary)',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'border-color 0.2s, background 0.2s, color 0.2s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'var(--dp-bg-card)';
      e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
      e.currentTarget.style.color = 'var(--dp-text-primary)';
    }}
    onMouseLeave={(e) => {
      if (!active) {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
        e.currentTarget.style.color = 'var(--dp-text-secondary)';
      }
    }}
  >
    {children}
  </button>
);

// Breadcrumb mapping: path → { groupKey (i18n), pageKey (i18n) }
// Groups match sidebar hierarchy: Visão Geral, Meus Canais, Biblioteca, Ferramentas, Tendências, Academia, Suporte, Admin
const BREADCRUMB_MAP = {
  '/':                  { groupKey: 'breadcrumb.visao_geral',    pageKey: 'nav.dashboard' },
  '/canais':            { groupKey: 'breadcrumb.meus_canais',    pageKey: 'sidebar.channels_management' },
  '/prompts':           { groupKey: 'breadcrumb.biblioteca',     pageKey: 'nav.prompts' },
  '/referencias':       { groupKey: 'breadcrumb.biblioteca',     pageKey: 'nav.references' },
  '/biblioteca-midia':  { groupKey: 'breadcrumb.biblioteca',     pageKey: 'nav.media_library' },
  '/audio-generator':   { groupKey: 'breadcrumb.ferramentas',    pageKey: 'nav.audio_generator' },
  '/transcricao':       { groupKey: 'breadcrumb.ferramentas',    pageKey: 'nav.transcricao' },
  '/darkplanner-app':   { groupKey: 'breadcrumb.ferramentas',    pageKey: 'sidebar.download_app' },
  '/text-tools':        { groupKey: 'breadcrumb.ferramentas',    pageKey: 'nav.text_tools' },
  '/quadro-ideias':     { groupKey: 'breadcrumb.ferramentas',    pageKey: 'nav.idea_board' },
  '/extension':         { groupKey: 'breadcrumb.ferramentas',    pageKey: 'nav.tools' },
  '/videos-virais':     { groupKey: 'breadcrumb.tendencias',     pageKey: 'nav.viral_videos' },
  '/nichos-virais':     { groupKey: 'breadcrumb.tendencias',     pageKey: 'nav.viral_niches' },
  '/lista-nichos':      { groupKey: 'breadcrumb.tendencias',     pageKey: 'nav.viral_niches' },
  '/canais-removidos':  { groupKey: 'breadcrumb.tendencias',     pageKey: 'nav.removed_channels' },
  '/dobra-nicho':           { groupKey: 'breadcrumb.tendencias', pageKey: 'dobraNicho.title' },
  '/dobra-nicho/historico': { groupKey: 'breadcrumb.tendencias', pageKey: 'dobraNicho.title' },
  '/insights':          { groupKey: 'breadcrumb.tendencias',     pageKey: 'sidebar.insights_channel' },
  '/academia':          { groupKey: 'breadcrumb.academia',       pageKey: 'nav.academy' },
  '/comunidade':        { groupKey: 'breadcrumb.suporte',        pageKey: 'sidebar.community_support' },
  '/configuracoes':     { groupKey: 'breadcrumb.configuracoes',  pageKey: 'nav.settings' },
  '/perfil':            { groupKey: 'breadcrumb.configuracoes',  pageKey: 'nav.settings' },
  '/meu-plano':         { groupKey: 'breadcrumb.visao_geral',    pageKey: 'nav.my_plan' },
  '/super-admin':       { groupKey: 'breadcrumb.administracao',  pageKey: 'nav.super_admin' },
  '/notifications':     { groupKey: 'breadcrumb.administracao',  pageKey: 'nav.notifications' },
  '/modelagem':         { groupKey: 'breadcrumb.ferramentas',    pageKey: 'nav.modeling' },
  '/gestao-financeira': { groupKey: 'breadcrumb.administracao',  pageKey: 'nav.financial' },
};

const Header = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { isPrivate, togglePrivacy } = usePrivacy();
  const { sidebarWidth, isMobile, toggleMobileMenu, isCollapsed, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const [userData, setUserData] = useState(_profileCache);

  // startsWith cobre códigos regionalizados (ex.: 'pt-BR').
  const currentLang = LANGUAGES.find(l => i18n.language?.startsWith(l.code)) || LANGUAGES[0];
  const breadcrumb = BREADCRUMB_MAP[location.pathname];
  // 3º nível dinâmico: na Dobra de Nicho, quando há uma análise aberta (a página
  // reflete isso na URL via ?a=<id>), o breadcrumb mostra "... › Análise".
  const subCrumb = (location.pathname === '/dobra-nicho' && new URLSearchParams(location.search).get('a'))
    ? t('dobraNicho.breadcrumb_analysis')
    : null;

  // Fetch user profile data for avatar (atualiza o cache de módulo em silêncio)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { _profileCache = res.data; setUserData(res.data); })
      .catch(() => {});
  }, []);

  // Close dropdown on outside click + ESC
  useEffect(() => {
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: isMobile ? 0 : `${sidebarWidth}px`,
      right: 0,
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '0 16px' : '0 32px',
      borderBottom: 'none',
      gap: '12px',
      background: isDarkMode ? 'rgba(10, 10, 10, 0.85)' : 'rgba(250, 250, 250, 0.85)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      zIndex: 50,
      transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: 'var(--dp-font)',
    }}>
      {/* Left side: hamburger (mobile) + breadcrumb */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '0',
        fontSize: '13px',
        fontFamily: 'var(--dp-font)',
        minWidth: 0,
      }}>
        {/* Botão de painel — abre/fecha o sidebar (desktop), antes do breadcrumb */}
        {!isMobile && (
          <div style={{ marginRight: '12px', flexShrink: 0 }}>
            <DarkTooltip
              content={isCollapsed ? t('sidebar.expand_sidebar') : t('sidebar.collapse_sidebar')}
              position="bottom"
            >
              <button
                onClick={toggleSidebar}
                aria-label={isCollapsed ? t('sidebar.expand_sidebar') : t('sidebar.collapse_sidebar')}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'ew-resize',
                  color: 'var(--dp-text-secondary)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--dp-bg-card)';
                  e.currentTarget.style.color = 'var(--dp-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--dp-text-secondary)';
                }}
              >
                {isCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
              </button>
            </DarkTooltip>
          </div>
        )}

        {/* Hamburger menu button — mobile only */}
        {isMobile && (
          <button
            onClick={toggleMobileMenu}
            style={{
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1px solid var(--dp-border-medium)',
              borderRadius: 'var(--dp-radius-sm)',
              color: 'var(--dp-text-primary)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--dp-bg-card)';
              e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
            }}
            aria-label="Abrir menu"
          >
            <Menu size={18} />
          </button>
        )}

        {/* Breadcrumb — show page name only on mobile */}
        {breadcrumb ? (
          isMobile ? (
            <span style={{
              color: 'var(--dp-text-secondary)',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {t(breadcrumb.pageKey)}
            </span>
          ) : (
            <>
              <span style={{ color: 'var(--dp-text-tertiary)', whiteSpace: 'nowrap' }}>
                {t(breadcrumb.groupKey)}
              </span>
              <span style={{ color: 'var(--dp-text-tertiary)', margin: '0 8px', userSelect: 'none' }}>
                ›
              </span>
              {subCrumb ? (
                // Com subcrumb (ex.: análise aberta), o crumb da PÁGINA vira link
                // que volta pro nível dela (sem query, ?a=) — ex.: "Dobra de Nicho"
                // -> volta pro hero da Dobra.
                <button
                  onClick={() => navigate(location.pathname)}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--dp-text-tertiary)', fontWeight: 500, whiteSpace: 'nowrap', transition: 'color 0.15s' }}
                >
                  {t(breadcrumb.pageKey)}
                </button>
              ) : (
                <span style={{
                  color: 'var(--dp-text-secondary)',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {t(breadcrumb.pageKey)}
                </span>
              )}
              {subCrumb && (
                <>
                  <span style={{ color: 'var(--dp-text-tertiary)', margin: '0 8px', userSelect: 'none' }}>›</span>
                  <span style={{ color: 'var(--dp-text-secondary)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {subCrumb}
                  </span>
                </>
              )}
            </>
          )
        ) : (
          <span style={{ color: 'var(--dp-text-tertiary)' }}>&nbsp;</span>
        )}
      </div>

      {/* Right side controls: [privacidade] [idioma] [sino] [tema] [pill do usuário] */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px', flexShrink: 0 }}>

      {/* Modo privado — mascara nomes/números sensíveis */}
      <RoundIconButton
        onClick={togglePrivacy}
        ariaLabel={isPrivate ? t('sidebar.private_mode_active') : t('sidebar.private_mode')}
        active={isPrivate}
        size={isMobile ? 28 : 32}
      >
        {isPrivate ? <EyeOff size={14} /> : <Eye size={14} />}
      </RoundIconButton>

      {/* Language selector — bandeira do idioma atual; abre "All Languages" */}
      <div ref={langRef} style={{ position: 'relative', flexShrink: 0 }}>
        <RoundIconButton
          onClick={() => setLangOpen(!langOpen)}
          ariaLabel={t('header.change_language')}
          active={langOpen}
          size={isMobile ? 28 : 32}
        >
          <FlagIcon code={currentLang.flagCode} size={isMobile ? '14px' : '16px'} rounded />
        </RoundIconButton>

        {/* Dropdown de idiomas */}
        {langOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            background: 'var(--dp-bg-elevated)',
            border: '1px solid var(--dp-border-medium)',
            borderRadius: 'var(--dp-radius-sm)',
            padding: '4px',
            minWidth: '190px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
          }}>
            <div style={{
              padding: '8px 10px 4px',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: 'var(--dp-text-tertiary)',
              fontFamily: 'var(--dp-font)',
            }}>
              {t('header.all_languages')}
            </div>
            {LANGUAGES.map((lang) => {
              const isActive = i18n.language?.startsWith(lang.code);
              return (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: 'none',
                    background: isActive ? 'var(--dp-blue-subtle)' : 'transparent',
                    color: isActive ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                    cursor: 'pointer',
                    fontSize: '12.5px',
                    fontWeight: isActive ? 600 : 400,
                    fontFamily: 'var(--dp-font)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
                      e.currentTarget.style.color = 'var(--dp-text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--dp-text-secondary)';
                    }
                  }}
                >
                  <FlagIcon code={lang.flagCode} size="18px" rounded />
                  {lang.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Notification bell */}
      <NotificationBell />

      {/* Tema claro/escuro */}
      <RoundIconButton
        onClick={toggleTheme}
        ariaLabel={t('header.toggle_theme')}
        size={isMobile ? 28 : 32}
      >
        {isDarkMode ? <Moon size={14} /> : <Sun size={14} />}
      </RoundIconButton>

      {/* Pill do plano + avatar com menu (Meu Perfil / Meu Plano / API / Sair) */}
      <HeaderUserMenu userData={userData} />

      </div>{/* end right side controls */}
    </header>
  );
};

export default Header;
